import type { TOptions, TPlayer } from 'video.js'
import type { Configuration } from './index.h'

import videojs from 'video.js'
import { defaultPlayer } from '|>/components'
import { isPlayerDebugMode } from '|>/shared/constants/debug'
import { flow } from '|>/shared/fn'
import { connectMQ } from '|>/shared/mq'
import { deepMerge } from '|>/shared/utils/deepMerge'
import * as chromecast from './addons/chromecast'
import * as cmcd from './addons/cmcd'
import * as dolby from './addons/dolby'
import * as eme from './addons/eme'
import * as instance from './addons/instance'
import * as persistVolume from './addons/persist-volume'
import * as playbackHeaders from './addons/playback-headers'
import * as playbackSession from './addons/playback-session'
import * as playlistRetryLimit from './addons/playlist-retry-limit'
import * as smartlib from './addons/smartlib'
import * as streamStats from './addons/stream-stats'
import * as textTrackCustomSettings from './addons/text-track-custom-settings'
import * as wakeLock from './addons/wake-lock'

/**
 * Default and mandatory options for all player instances
 */
export const defaults: TOptions = {
  autoplay: 'any', // maybe get it from option? (defined second time)
  controls: true,
  language: 'en',
  preload: 'auto',
  preloadTextTracks: true,
  playsinline: videojs.browser.IS_IOS, // only iOS or always true? https://developer.mozilla.org/en-US/docs/Web/HTML/Element/video#playsinline
  techOrder: ['Html5'],
  muted: videojs.browser.IS_IOS,
  debug: isPlayerDebugMode,
  inactivityTimeout: 3000,
  userActions: {
    hotkeys: {
      fullscreenKey: (e: KeyboardEvent) => e.code === 'KeyF',
      muteKey: (e: KeyboardEvent) => e.code === 'KeyM',
      playPauseKey: (e: KeyboardEvent) => e.code === 'Space' || e.key === ' ',
    },
  },
  html5: {
    vhs: {
      experimentalUseMMS: true,
      handlePartialData: true,
      // enableLowInitialPlaylist: true,
      parse708captions: true,
      handleManifestRedirects: true,
      cacheEncryptionKeys: true,
      debug: isPlayerDebugMode,

      // related to https://setplexapps.atlassian.net/browse/MW-6592
      // which got rejected due to inactivity...
      // send cookies with cross-origin requests
      // only of `with-credentials` param is set
      // withCredentials: isWithCredentials, // TODO:
    },

    // do not use native audio tracks
    // this will prevent Safari from using native audio tracks.
    // see https://setplexapps.atlassian.net/browse/FP-3032
    nativeAudioTracks: false,
  },
}

export function createPlayer(
  el: Element,
  options: TOptions,
  {
    using = defaultPlayer,
    onReady,
    chromecast: chromecastConfiguration,
    dolby: dolbyConfiguration,
    cmcd: cmcdConfiguration,
    smartlib: smartlibConfiguration,
  }: Configuration
): TPlayer | Promise<TPlayer> {
  // addons configuration step
  // this is where addon can adjust player's options
  // could return config synchronously or as a promise (if async initialization is needed)

  const config = flow(
    deepMerge({}, defaults, options),
    eme.configure,
    persistVolume.configure,
    textTrackCustomSettings.configure,
    playbackSession.configure,
    playbackHeaders.configure,
    cmcd.configure(cmcdConfiguration),
    chromecast.configure(chromecastConfiguration),
    smartlib.configure(smartlibConfiguration),
    instance.configure(using),
    streamStats.configure,
    dolby.configure(dolbyConfiguration),
    wakeLock.configure
  )

  // player creation step
  // this step is separated to a function to allow both sync and async initialization
  // this is like an "await" tear point, but without actually use async/await syntax
  // because if function is marked as async - it will always return a promise,
  // effectively making it impossible to return a player instance synchronously
  function creator(config: TOptions): TPlayer {
    instance.replace(using)
    const player = videojs(el, config, () => {
      onReady?.(player)
      connectMQ(player)
    }) as unknown as TPlayer

    // addons injection step
    // this is where addon can adjust player's instance
    return flow(
      player,
      eme.inject,
      playbackSession.inject,
      playbackHeaders.inject,
      cmcd.inject, // injects cmcd only if it enabled
      chromecast.inject, // injects chromecast only if it enabled
      smartlib.inject(smartlibConfiguration), // injects smartlib only if it enabled
      persistVolume.inject,
      textTrackCustomSettings.inject,
      streamStats.inject,
      playlistRetryLimit.inject,
      dolby.inject,
      wakeLock.inject
    )
  }

  // return player instance or promise of player instance
  return config instanceof Promise ? config.then(creator) : creator(config)
}
